import React from 'react'
import { DataViewContext } from '@pushly/aqe/lib/components/data-view/context'
import { DataView } from '@pushly/aqe/lib/components/data-view/data-view'
import { DataSourceResponse, DataViewProps } from '@pushly/aqe/lib/components/data-view/types'
import { useDataViewReducer } from '@pushly/aqe/lib/components/data-view/reducer'
import { useService } from '@pushly/aqe/lib/hooks'
import { StatusBadge } from '@pushly/aqe/lib/components/badges'
import { EventWebhook } from '@pushly/models/lib/structs/event-webhooks/event-webhook'
import { Space, Table, Tag, Tooltip } from 'antd'
import { Button } from '@pushly/aqe/lib/components/button/button'
import { DomainWebhookService } from '../../services/domain-webhook.service'
import { ApiVersion } from '../../enums/api-version.enum'
import { formatWebhookEventTypeLabel } from './webhook-event-type-label'
import { FeatureEmptyState } from '../icons/feature-icon'

interface WebhookListProps {
    domainId: number
    onAddWebhook: () => void
    onEditWebhook: (webhook: EventWebhook) => void
    onArchiveWebhook: (webhook: EventWebhook) => void
    onToggleEnabled: (webhook: EventWebhook) => void
}

const MAX_URL_DISPLAY_LENGTH = 80

export const WebhookList = (props: WebhookListProps) => {
    const { domainId, onAddWebhook, onEditWebhook, onArchiveWebhook, onToggleEnabled } = props

    const webhookService = useService(DomainWebhookService)

    const [context, dispatch, requestDataSourceReload] = useDataViewReducer<EventWebhook>({
        id: 'domain-event-webhooks',
        defaultPageSize: 10,
    })

    const fetchWebhooks: DataViewProps<EventWebhook>['dataSourceLoader'] = async (filters, tableState) => {
        const apiOptions = {
            showLoadingScreen: true,
            cancellationKey: 'domain-event-webhooks.fetch',
            version: ApiVersion.V3,
        }

        const res = await webhookService.list(domainId, apiOptions)
        const dataViewResponse: DataSourceResponse = {
            meta: {},
            cancelled: false,
            ...res,
            data: [],
        }

        if (!res.cancelled) {
            if (res.ok && res.data) {
                dataViewResponse.data = res.data.map((data) => ({ data }))
            } else if (res.error) {
                console.warn(res.error)
            }
        }

        return dataViewResponse
    }

    const truncateUrl = (url: string): string => {
        if (url.length <= MAX_URL_DISPLAY_LENGTH) return url
        return url.substring(0, MAX_URL_DISPLAY_LENGTH) + '…'
    }

    return (
        <DataViewContext.Provider value={{ state: context, dispatch }}>
            <DataView
                dataSourceLoader={fetchWebhooks}
                hideRefresh={true}
                hideAutoRefreshOptions={true}
                actions={
                    <Button type="primary" size="middle" onClick={onAddWebhook}>
                        Add Webhook
                    </Button>
                }
                title="Webhooks"
                table={{
                    rowKey: (row) => row.id!,
                    noData: (
                        <FeatureEmptyState
                            slot="rail:domain:event-webhooks"
                            text="Click 'Add Webhook' to create your first webhook"
                            style={{ padding: '20px 0 5px 0' }}
                        />
                    ),
                    hideFooter: false,
                    rowClassName: 'data-view-row-collapsed',
                    className: 'aqe-table-well aqe-table-well-shadow',
                }}
            >
                <Table.Column
                    align="center"
                    key="enabled"
                    title="Status"
                    width={70}
                    dataIndex={['data', 'enabled']}
                    render={(enabled: boolean) => <StatusBadge status={enabled ? 'ENABLED' : 'DISABLED'} />}
                />

                <Table.Column
                    key="url"
                    title="URL"
                    dataIndex={['data', 'url']}
                    render={(url: string) => {
                        if (url.length > MAX_URL_DISPLAY_LENGTH) {
                            return (
                                <Tooltip title={url}>
                                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{truncateUrl(url)}</span>
                                </Tooltip>
                            )
                        }
                        return <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{url}</span>
                    }}
                />

                <Table.Column
                    key="event_types"
                    title="Event Types"
                    width={260}
                    dataIndex={['data', 'event_types']}
                    render={(eventTypes: string[]) => (
                        <Space size={4} wrap>
                            {[...(eventTypes ?? [])].sort().map((type) => (
                                <Tag key={type}>{formatWebhookEventTypeLabel(type)}</Tag>
                            ))}
                        </Space>
                    )}
                />

                <Table.Column
                    align="right"
                    key="actions"
                    title=""
                    width={120}
                    dataIndex="data"
                    render={(webhook: EventWebhook) => (
                        <Button
                            size="small"
                            onClick={() => onEditWebhook(webhook)}
                            actions={[
                                {
                                    text: webhook.enabled ? 'Pause' : 'Resume',
                                    onClick: () => onToggleEnabled(webhook),
                                },
                                { divider: true },
                                {
                                    text: 'Archive',
                                    danger: true,
                                    onClick: () => onArchiveWebhook(webhook),
                                },
                            ]}
                        >
                            Edit
                        </Button>
                    )}
                />
            </DataView>
        </DataViewContext.Provider>
    )
}

export { WebhookListProps }
